import { useEffect, useRef, useState, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Search } from 'lucide-react';
import { useLazyGetOrdersQuery } from '@features/orders/api';
import { cn } from '@/lib/utils';
import { FetchErrorState } from './FetchErrorState';

/**
 * Global order search in the top bar. Mirrors /.../OrderSearchBar:
 * - 2-char minimum, 500ms debounce.
 * - Calls `GET /order/v1/orders?id=<term>&page=1&pageSize=5`.
 * - Dropdown lists matching shortIds; clicking one navigates to detail.
 * - Single-result auto-navigate when the term exactly matches the row's
 * `shortId` or `id` (case-insensitive).
 * - Click-outside closes the dropdown and clears the term.
 */
export function OrderSearchBar(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement>(null);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [search, { data, isFetching, isError }] = useLazyGetOrdersQuery();

  // 500ms debounce — feeds the lazy query. < 2 chars clears.
  useEffect(() => {
    if (term.length < 2) {
      setDebounced('');
      setOpen(false);
      return;
    }
    const handle = setTimeout(() => setDebounced(term), 500);
    return () => clearTimeout(handle);
  }, [term]);

  // Fire the query when the debounced term changes.
  useEffect(() => {
    if (debounced.length < 2) return;
    let cancelled = false;
    (async (): Promise<void> => {
      try {
        const resp = await search({ id: debounced, page: 1, pageSize: 5 }).unwrap();
        if (cancelled) return;
        const orders = resp.orders ?? [];
        // Auto-navigate on a single exact match.
        if (orders.length === 1) {
          const only = orders[0]!;
          const low = debounced.toLowerCase();
          if ((only.shortId ?? '').toLowerCase() === low || only.id.toLowerCase() === low) {
            navigate(`/orders/${only.id}`);
            setTerm('');
            setOpen(false);
            return;
          }
        }
        setOpen(true);
      } catch {
        // network/server errors: open the dropdown so the user sees the
        // error row with a Retry rather than a silent failure.
        // The toast comes from baseQuery's error handler already.
        setOpen(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [debounced, navigate, search]);

  // Click-outside closes + clears.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent): void => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false);
        setTerm('');
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const onSelect = (orderId: string): void => {
    navigate(`/orders/${orderId}`);
    setTerm('');
    setOpen(false);
  };

  const orders = data?.orders ?? [];

  return (
    <div className="relative w-full" ref={wrapperRef}>
      <label htmlFor="topbar-search" className="sr-only">
        {t('navigation:topbar.quickSearchLabel', { defaultValue: 'Quick search' })}
      </label>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      {isFetching && (
        <Loader2
          className="pointer-events-none absolute right-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground"
          data-testid="topbar-search-spinner"
        />
      )}
      <input
        id="topbar-search"
        type="text"
        autoComplete="off"
        placeholder={t('navigation:topbar.quickSearch', { defaultValue: 'Search for Order ID' })}
        aria-label={t('navigation:topbar.quickSearchLabel', { defaultValue: 'Quick search' })}
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            setTerm('');
            setOpen(false);
          }
        }}
        className={cn(
          'h-9 w-full rounded-md border border-input bg-muted/40 pl-9 pr-9 text-sm placeholder:text-muted-foreground',
          'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        )}
        data-testid="topbar-order-search-input"
      />

      {open && debounced.length >= 2 && !isFetching && (
        <ul
          role="listbox"
          className="absolute left-0 right-0 top-full z-50 mt-1 max-h-72 overflow-y-auto rounded-md border bg-popover py-1 text-sm text-popover-foreground shadow-md"
          data-testid="topbar-order-search-menu"
        >
          {orders.length > 0 ? (
            orders.map((o) => (
              <li
                key={o.id}
                role="option"
                aria-selected={false}
                tabIndex={0}
                onClick={() => onSelect(o.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onSelect(o.id);
                  }
                }}
                className="cursor-pointer truncate px-3 py-2 hover:bg-accent hover:text-accent-foreground"
                data-testid={`topbar-order-search-result-${o.id}`}
              >
                {o.shortId ?? o.id}
              </li>
            ))
          ) : isError ? (
            <li className="px-3 py-2" data-testid="topbar-order-search-error">
              <FetchErrorState
                variant="plain"
                message={t('navigation:topbar.searchError', {
                  defaultValue: 'Failed to search orders. Please try again.',
                })}
                onRetry={() => void search({ id: debounced, page: 1, pageSize: 5 })}
                retryTestId="topbar-order-search-retry-button"
              />
            </li>
          ) : (
            <li
              className="px-3 py-2 text-muted-foreground"
              data-testid="topbar-order-search-no-results"
            >
              {t('navigation:topbar.noSearchResults', { defaultValue: 'No results found' })}
            </li>
          )}
        </ul>
      )}
    </div>
  );
}
